import React, { useState, useEffect, useRef } from'react';
import { useParams, Link, useLocation } from'react-router-dom';
import { motion } from'framer-motion';
import { Calendar, MapPin, Clock, PoundSterling, Music, Globe, ArrowLeft, ExternalLink, User, Mail, Phone, Share2, Ticket, ShoppingCart, Pencil, QrCode, Download, Users } from'lucide-react';
import { Button } from'../components/ui/button';
import { Badge } from'../components/ui/badge';
import { Input } from'../components/ui/input';
import { Label } from'../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from'../components/ui/dialog';
import { toast } from'sonner';
import { getEventImage } from'../utils/eventImages';
import { TicketPurchaseModal } from'../components/tickets';
import { useTicketCart } from'../context/TicketCartContext';
import { useAuth } from'../context/AuthContext';

import { API_URL } from'../utils/api';

// Component to safely render and execute widget code including scripts
function TicketWidget({ code }) {
 const containerRef = useRef(null);

 useEffect(() => {
 if (!containerRef.current || !code) return;

 // Clear previous content
 containerRef.current.innerHTML ='';

 // Create a temporary container to parse the HTML
 const tempDiv = document.createElement('div');
 tempDiv.innerHTML = code;

 // Extract scripts
 const scripts = tempDiv.querySelectorAll('script');
 
 // Get non-script HTML content
 const nonScriptContent = code.replace(/<script[\s\S]*?<\/script>/gi,'');
 
 // Add non-script content first
 containerRef.current.innerHTML = nonScriptContent;

 // Track loaded scripts for cleanup
 const loadedScripts = [];

 // Function to load scripts sequentially
 const loadScriptsSequentially = async () => {
 for (const originalScript of scripts) {
 const newScript = document.createElement('script');
 
 // Copy attributes
 Array.from(originalScript.attributes).forEach(attr => {
 newScript.setAttribute(attr.name, attr.value);
 });

 const src = originalScript.getAttribute('src');
 
 if (src) {
 // External script - wait for it to load
 await new Promise((resolve, reject) => {
 newScript.onload = resolve;
 newScript.onerror = reject;
 document.body.appendChild(newScript);
 loadedScripts.push(newScript);
 });
 } else if (originalScript.textContent) {
 // Inline script - modify DOMContentLoaded to execute immediately
 let scriptContent = originalScript.textContent;
 
 // Replace DOMContentLoaded listener with immediate execution
 if (scriptContent.includes('DOMContentLoaded')) {
 scriptContent = scriptContent.replace(
 /document\.addEventListener\(['"]DOMContentLoaded['"],\s*function\s*\([^)]*\)\s*\{/g,
'(function() {'
 );
 // Also handle the closing
 scriptContent = scriptContent.replace(/\}\);(\s*)$/g,'})();$1');
 }
 
 newScript.textContent = scriptContent;
 document.body.appendChild(newScript);
 loadedScripts.push(newScript);
 }
 }
 };

 loadScriptsSequentially().catch(console.error);

 // Cleanup function
 return () => {
 loadedScripts.forEach(script => {
 if (script.parentNode) {
 script.parentNode.removeChild(script);
 }
 });
 };
 }, [code]);

 return (
 <div 
 ref={containerRef}
 className="ticket-widget-container min-h-[200px]"
 data-testid="ticket-widget"
 />
 );
}

export default function EventDetailPage() {
 const { id, hostSlug, eventSlug } = useParams();
 const location = useLocation();
 const { user } = useAuth();
 const [event, setEvent] = useState(null);
 const [loading, setLoading] = useState(true);
 const [club, setClub] = useState(null);
 const [showTicketModal, setShowTicketModal] = useState(false);
 const [showExternalTicketModal, setShowExternalTicketModal] = useState(false);
 const { setCartOpen } = useTicketCart();
 const ticketsSectionRef = useRef(null);
 
 // Slug editing state (admin only)
 const [editingSlug, setEditingSlug] = useState(false);
 const [slugForm, setSlugForm] = useState({ slug:'', host_slug:'' });
 const [slugSaving, setSlugSaving] = useState(false);
 
 const isAdmin = user?.is_admin;
 
 // Check if user can manage this event (admin or club owner)
 const canManageEvent = isAdmin || (user && club && club.owner_id === user.user_id);

 // Determine the lookup path - either slug format or ID
 const lookupPath = hostSlug && eventSlug 
 ? `${hostSlug}/${eventSlug}` 
 : id;

 useEffect(() => {
 fetchEvent();
 // Scroll to top when page loads (unless #tickets hash is present)
 if (location.hash !== '#tickets') {
 window.scrollTo(0, 0);
 }
 }, [lookupPath]);

 // Auto-open ticket modal if #tickets hash is present and widget code exists
 useEffect(() => {
 if (location.hash === '#tickets' && event) {
 // Scroll to top first, then open modal
 window.scrollTo(0, 0);
 // If there's a widget code or external URL, open the modal
 if (event.ticket_widget_code || event.external_ticket_url) {
 setTimeout(() => {
 setShowExternalTicketModal(true);
 }, 300);
 } else if (ticketsSectionRef.current) {
 // Otherwise scroll to the tickets section
 ticketsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }
 }
 }, [location.hash, event]);

 const fetchEvent = async () => {
 setLoading(true);
 try {
 const response = await fetch(`${API_URL}/api/events/detail/${lookupPath}`);
 if (response.ok) {
 const data = await response.json();
 setEvent(data);
 
 // If it's a club event, fetch club details
 if (data.club_id && data.type ==='club') {
 const clubRes = await fetch(`${API_URL}/api/clubs/${data.club_id}`);
 if (clubRes.ok) {
 const clubData = await clubRes.json();
 setClub(clubData);
 }
 }
 } else {
 toast.error('Event not found');
 }
 } catch (error) {
 console.error('Error fetching event:', error);
 toast.error('Failed to load event');
 } finally {
 setLoading(false);
 }
 };

 const handleShare = async () => {
 const url = window.location.href;
 if (navigator.share) {
 try {
 await navigator.share({
 title: event?.title,
 text: `Check out this event: ${event?.title}`,
 url: url
 });
 } catch (err) {
 // User cancelled or error
 copyToClipboard(url);
 }
 } else {
 copyToClipboard(url);
 }
 };

 const copyToClipboard = (text) => {
 navigator.clipboard.writeText(text);
 toast.success('Link copied to clipboard!');
 };

 // Export attendees function
 const exportAttendees = async () => {
   if (!event?.event_id) return;
   
   const token = localStorage.getItem('token');
   try {
     const res = await fetch(`${API_URL}/api/tickets/attendees/${event.event_id}`, {
       headers: { 'Authorization': `Bearer ${token}` }
     });
     
     if (res.ok) {
       const data = await res.json();
       if (!data.attendees || data.attendees.length === 0) {
         toast.info('No attendees to export yet');
         return;
       }
       
       const headers = ['Name', 'Email', 'Ticket Type', 'Quantity', 'Status', 'Checked In', 'Purchase Date', 'Booking Ref'];
       const rows = data.attendees.map(a => [
         a.name || '',
         a.email || '',
         a.ticket_type || '',
         a.quantity || 1,
         a.status || '',
         a.checked_in ? 'Yes' : 'No',
         a.purchase_date ? new Date(a.purchase_date).toLocaleDateString() : '',
         a.booking_ref || ''
       ]);
       
       const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
       
       const blob = new Blob([csv], { type: 'text/csv' });
       const url = URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url;
       a.download = `attendees-${event.slug || event.event_id}-${new Date().toISOString().split('T')[0]}.csv`;
       a.click();
       URL.revokeObjectURL(url);
       
       toast.success(`Exported ${data.attendees.length} attendees`);
     } else {
       const error = await res.json();
       toast.error(error.detail || 'Failed to export attendees');
     }
   } catch (error) {
     console.error('Error exporting attendees:', error);
     toast.error('Network error');
   }
 };

 const formatDate = (dateStr) => {
 const date = new Date(dateStr);
 return date.toLocaleDateString('en-GB', {
 weekday:'long',
 day:'numeric',
 month:'long',
 year:'numeric'
 });
 };

 // Build the map URL for OpenStreetMap embed
 const getMapUrl = () => {
 if (event?.latitude && event?.longitude) {
 return `https://www.openstreetmap.org/export/embed.html?bbox=${event.longitude - 0.01},${event.latitude - 0.01},${event.longitude + 0.01},${event.latitude + 0.01}&layer=mapnik&marker=${event.latitude},${event.longitude}`;
 }
 // For club events, use club location
 if (club?.latitude && club?.longitude) {
 return `https://www.openstreetmap.org/export/embed.html?bbox=${club.longitude - 0.01},${club.latitude - 0.01},${club.longitude + 0.01},${club.latitude + 0.01}&layer=mapnik&marker=${club.latitude},${club.longitude}`;
 }
 return null;
 };

 const getDirectionsUrl = () => {
 const lat = event?.latitude || club?.latitude;
 const lng = event?.longitude || club?.longitude;
 const location = event?.location || club?.location || club?.address;
 
 if (lat && lng) {
 return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
 } else if (location) {
 return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`;
 }
 return null;
 };

 // Slug editing functions (admin only)
 const formatSlugInput = (value) => {
 return value
 .toLowerCase()
 .replace(/[^a-z0-9\s-]/g,'')
 .replace(/\s+/g,'-')
 .replace(/-+/g,'-')
 .substring(0, 60);
 };

 const openSlugEditor = () => {
 setSlugForm({
 slug: event?.slug ||'',
 host_slug: event?.host_slug ||''
 });
 setEditingSlug(true);
 };

 const handleSlugSave = async () => {
 const token = localStorage.getItem('token');
 setSlugSaving(true);
 
 try {
 const res = await fetch(`${API_URL}/api/admin/events/${event.event_id}/slugs`, {
 method:'PUT',
 headers: {
'Authorization': `Bearer ${token}`,
'Content-Type':'application/json'
 },
 body: JSON.stringify({
 slug: slugForm.slug,
 host_slug: slugForm.host_slug
 })
 });

 if (res.ok) {
 const data = await res.json();
 toast.success(`URL updated! Redirecting...`);
 
 // Update local state
 setEvent({ ...event, slug: data.slug, host_slug: data.host_slug, full_slug: data.full_slug });
 setEditingSlug(false);
 
 // Redirect to new URL after a brief delay
 setTimeout(() => {
 window.location.href = data.url;
 }, 1000);
 } else {
 const error = await res.json();
 toast.error(error.detail ||'Failed to update URL');
 }
 } catch (error) {
 console.error('Error updating slugs:', error);
 toast.error('Network error');
 } finally {
 setSlugSaving(false);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-[var(--bg-base)]" data-testid="event-detail-loading">
 <div className="bg-[var(--bg-surface)] border-b border-stone-200 py-16">
 <div className="container mx-auto px-6 lg:px-12">
 <div className="h-8 w-48 bg-stone-200 rounded animate-pulse mb-4" />
 <div className="h-12 w-3/4 bg-stone-200 rounded animate-pulse" />
 </div>
 </div>
 <div className="container mx-auto px-6 lg:px-12 py-12">
 <div className="grid lg:grid-cols-3 gap-8">
 <div className="lg:col-span-2 space-y-6">
 <div className="h-64 bg-slate-200 rounded-2xl animate-pulse" />
 <div className="h-32 bg-slate-200 rounded-2xl animate-pulse" />
 </div>
 <div className="h-96 bg-slate-200 rounded-2xl animate-pulse" />
 </div>
 </div>
 </div>
 );
 }

 if (!event) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <div className="text-center">
 <Calendar className="w-20 h-20 text-slate-300 mx-auto mb-4" />
 <h2 className="text-2xl font-bold text-slate-700 mb-2">Event not found</h2>
 <p className="text-slate-300 mb-6">This event may have been removed or the link is incorrect.</p>
 <Link to="/events">
 <Button className="bg-violet-600 hover:bg-violet-500">
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Events
 </Button>
 </Link>
 </div>
 </div>
 );
 }

 const mapUrl = getMapUrl();
 const directionsUrl = getDirectionsUrl();
 const locationDisplay = event.location || club?.location || club?.address || 
 [club?.city, club?.region, club?.country].filter(Boolean).join(',');

 return (
 <div className="min-h-screen bg-[var(--bg-base)]" data-testid="event-detail-page">
 {/* Header */}
 <div className="bg-[var(--bg-surface)] border-b border-stone-200 py-12 lg:py-16">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 >
 <Link 
 to="/events" 
 className="inline-flex items-center text-stone-600 hover:text-violet-700 mb-4 transition-colors"
 data-testid="back-to-events-link"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Events
 </Link>
 
 <div className="flex flex-wrap items-start gap-3 mb-4">
 <Badge className={event.type ==='community' ?'bg-blue-500 text-white' :'bg-violet-500 text-white'}>
 {event.type ==='community' ? (
 <>
 <Globe className="w-3 h-3 mr-1" />
 Community Event
 </>
 ) : (
 <>
 <Music className="w-3 h-3 mr-1" />
 Club Event
 </>
 )}
 </Badge>
 {event.distance && (
 <Badge variant="secondary" className="bg-stone-100 text-stone-700">
 <MapPin className="w-3 h-3 mr-1" />
 {event.distance} miles away
 </Badge>
 )}
 
 {/* Event Management Buttons */}
 {canManageEvent && event.ticketing_enabled && (
 <>
 <Button
 size="sm"
 className="bg-emerald-600 hover:bg-emerald-500 text-white"
 onClick={() => window.open(`/events/${event.event_id}/checkin`, '_blank')}
 data-testid="checkin-btn"
 >
 <QrCode className="w-3 h-3 mr-1" />
 Check-In
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={exportAttendees}
 className="bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
 data-testid="export-btn"
 >
 <Download className="w-3 h-3 mr-1" />
 Export
 </Button>
 </>
 )}
 
 {isAdmin && (
 <Button
 variant="outline"
 size="sm"
 onClick={openSlugEditor}
 className="bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
 data-testid="edit-url-btn"
 >
 <Pencil className="w-3 h-3 mr-1" />
 Edit URL
 </Button>
 )}
 </div>
 
 <h1 
 className="text-3xl lg:text-4xl font-extrabold text-stone-900 mb-4" 
 data-testid="event-title"
 >
 {event.title}
 </h1>
 
 <div className="flex flex-wrap items-center gap-6 text-stone-600">
 <div className="flex items-center gap-2">
 <Calendar className="w-5 h-5 text-violet-600" />
 <span>{formatDate(event.event_date)}</span>
 </div>
 <div className="flex items-center gap-2">
 <Clock className="w-5 h-5 text-teal-600" />
 <span>{event.event_time}</span>
 </div>
 {event.price && (
 <div className="flex items-center gap-2">
 <PoundSterling className="w-5 h-5 text-green-600" />
 <span>£{event.price}</span>
 </div>
 )}
 </div>
 </motion.div>
 </div>
 </div>

 {/* Content */}
 <div className="container mx-auto px-6 lg:px-12 py-12">
 <div className="grid lg:grid-cols-3 gap-8">
 {/* Main Content */}
 <motion.div 
 className="lg:col-span-2 space-y-6"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.1 }}
 >
 {/* Event Image */}
 <div className="rounded-2xl overflow-hidden shadow-lg bg-slate-100">
 <img 
 src={getEventImage(event.event_id, event.image_url)} 
 alt={event.title}
 className="w-full h-auto max-h-[600px] object-contain mx-auto"
 data-testid="event-image"
 />
 </div>

 {/* Description */}
 <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
 <h2 className="text-xl font-bold text-slate-900 mb-4">About this Event</h2>
 <p className="text-slate-500 whitespace-pre-wrap" data-testid="event-description">
 {event.description ||'No description provided.'}
 </p>
 </div>
 
 {/* Ticket Widget CTA Banner - Opens Modal */}
 {event.ticket_widget_code && (
 <div 
 ref={ticketsSectionRef}
 id="tickets"
 className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 shadow-lg"
 >
 <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
 <div className="text-white text-center sm:text-left">
 <h2 className="text-xl font-bold mb-1 flex items-center gap-2 justify-center sm:justify-start">
 <Ticket className="w-5 h-5" />
 Get Tickets
 </h2>
 <p className="text-orange-100 text-sm">Secure your spot - tickets available via external provider</p>
 </div>
 <Button 
 onClick={() => setShowExternalTicketModal(true)}
 className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-8 py-3"
 data-testid="widget-tickets-btn"
 >
 <ShoppingCart className="w-4 h-4 mr-2" />
 Get Tickets
 </Button>
 </div>
 </div>
 )}

 {/* External Ticket URL (no widget code) */}
 {!event.ticket_widget_code && event.external_ticket_url && (
 <div 
 ref={!event.ticket_widget_code ? ticketsSectionRef : null}
 id="tickets"
 className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 shadow-lg"
 >
 <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
 <div className="text-white text-center sm:text-left">
 <h2 className="text-xl font-bold mb-1 flex items-center gap-2 justify-center sm:justify-start">
 <Ticket className="w-5 h-5" />
 Get Tickets
 </h2>
 <p className="text-orange-100 text-sm">Tickets available via external provider</p>
 </div>
 <a 
 href={event.external_ticket_url.startsWith('http') ? event.external_ticket_url : `https://${event.external_ticket_url}`}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center justify-center bg-white text-orange-600 hover:bg-orange-50 font-bold px-8 py-3 rounded-md transition-colors"
 data-testid="external-tickets-btn"
 >
 <ExternalLink className="w-4 h-4 mr-2" />
 Get Tickets
 </a>
 </div>
 </div>
 )}

 {event.ticket_url && !event.ticket_widget_code && !event.external_ticket_url && (
 <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 shadow-lg">
 <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
 <div className="text-white text-center sm:text-left">
 <h2 className="text-xl font-bold mb-1 flex items-center gap-2 justify-center sm:justify-start">
 <Ticket className="w-5 h-5" />
 Tickets Available
 </h2>
 <p className="text-green-100 text-sm">Secure your spot at this event</p>
 </div>
 <a 
 href={event.ticket_url.startsWith('http') ? event.ticket_url : `https://${event.ticket_url}`}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="buy-tickets-btn"
 >
 <Button className="bg-white text-green-600 hover:bg-green-50 font-bold px-8 py-3">
 Buy Tickets
 <ExternalLink className="w-4 h-4 ml-2" />
 </Button>
 </a>
 </div>
 </div>
 )}

 {/* Internal Ticketing (Line Dance Central tickets) */}
 {event.ticketing_enabled && !event.ticket_widget_code && !event.ticket_url && !event.external_ticket_url && (
 <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 shadow-lg">
 <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
 <div className="text-white text-center sm:text-left">
 <h2 className="text-xl font-bold mb-1 flex items-center gap-2 justify-center sm:justify-start">
 <Ticket className="w-5 h-5" />
 Get Tickets
 </h2>
 <p className="text-violet-100 text-sm">Secure your spot at this event</p>
 </div>
 <Button 
 onClick={() => setShowTicketModal(true)}
 className="bg-white text-violet-600 hover:bg-violet-50 font-bold px-8 py-3"
 data-testid="internal-buy-tickets-btn"
 >
 <ShoppingCart className="w-4 h-4 mr-2" />
 Buy Tickets
 </Button>
 </div>
 </div>
 )}

 {/* Club Info (for club events) */}
 {club && (
 <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
 <h2 className="text-xl font-bold text-slate-900 mb-4">Hosted by</h2>
 <Link 
 to={`/clubs/${club.slug || club.club_id}`}
 className="flex items-center gap-4 group"
 data-testid="event-club-link"
 >
 <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
 {club.image_url ? (
 <img src={club.image_url} alt={club.name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <Music className="w-8 h-8 text-slate-300" />
 </div>
 )}
 </div>
 <div>
 <h3 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
 {club.name}
 </h3>
 <p className="text-sm text-slate-300">
 {club.location || [club.city, club.region].filter(Boolean).join(',')}
 </p>
 </div>
 <ExternalLink className="w-5 h-5 text-slate-300 group-hover:text-violet-500 ml-auto" />
 </Link>
 </div>
 )}

 {/* Map Section */}
 {mapUrl && (
 <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
 <div className="p-4 border-b border-slate-100">
 <h2 className="text-xl font-bold text-slate-900">Location</h2>
 {locationDisplay && (
 <p className="text-slate-300 text-sm mt-1 flex items-center">
 <MapPin className="w-4 h-4 mr-1" />
 {locationDisplay}
 </p>
 )}
 </div>
 <div className="h-64 lg:h-80">
 <iframe
 src={mapUrl}
 width="100%"
 height="100%"
 style={{ border: 0 }}
 allowFullScreen
 loading="lazy"
 referrerPolicy="no-referrer-when-downgrade"
 title="Event Location Map"
 data-testid="event-map"
 />
 </div>
 {directionsUrl && (
 <div className="p-4 bg-slate-50">
 <a 
 href={directionsUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center text-violet-600 font-medium hover:text-violet-700"
 data-testid="get-directions-link"
 >
 <MapPin className="w-4 h-4 mr-2" />
 Get Directions
 <ExternalLink className="w-4 h-4 ml-1" />
 </a>
 </div>
 )}
 </div>
 )}

 {/* If no map but has location text */}
 {!mapUrl && locationDisplay && (
 <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
 <h2 className="text-xl font-bold text-slate-900 mb-2">Location</h2>
 <p className="text-slate-500 flex items-center">
 <MapPin className="w-5 h-5 mr-2 text-violet-500" />
 {locationDisplay}
 </p>
 {directionsUrl && (
 <a 
 href={directionsUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center text-violet-600 font-medium hover:text-violet-700 mt-4"
 data-testid="get-directions-link"
 >
 Get Directions
 <ExternalLink className="w-4 h-4 ml-2" />
 </a>
 )}
 </div>
 )}
 </motion.div>

 {/* Sidebar */}
 <motion.div 
 className="space-y-6"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 >
 {/* Quick Info Card */}
 <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
 <h3 className="font-bold text-slate-900 mb-4">Event Details</h3>
 
 <div className="space-y-4">
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
 <Calendar className="w-5 h-5 text-violet-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Date</p>
 <p className="font-medium text-slate-900">{formatDate(event.event_date)}</p>
 </div>
 </div>
 
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
 <Clock className="w-5 h-5 text-teal-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Time</p>
 <p className="font-medium text-slate-900">{event.event_time}</p>
 </div>
 </div>
 
 {event.price && (
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
 <PoundSterling className="w-5 h-5 text-green-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Price</p>
 <p className="font-medium text-slate-900">£{event.price}</p>
 </div>
 </div>
 )}
 
 {locationDisplay && (
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
 <MapPin className="w-5 h-5 text-slate-500" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Location</p>
 <p className="font-medium text-slate-900 text-sm">{locationDisplay}</p>
 </div>
 </div>
 )}

 {/* Contact Info (for community events) */}
 {event.contact && (
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
 <User className="w-5 h-5 text-blue-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Contact</p>
 <p className="font-medium text-slate-900 text-sm">{event.contact}</p>
 </div>
 </div>
 )}

 {event.contact_email && (
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
 <Mail className="w-5 h-5 text-blue-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Email</p>
 <a 
 href={`mailto:${event.contact_email}`}
 className="font-medium text-violet-600 hover:underline text-sm"
 >
 {event.contact_email}
 </a>
 </div>
 </div>
 )}

 {event.website && (
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
 <Globe className="w-5 h-5 text-purple-600" />
 </div>
 <div>
 <p className="text-sm text-slate-300">Website</p>
 <a 
 href={event.website.startsWith('http') ? event.website : `https://${event.website}`}
 target="_blank"
 rel="noopener noreferrer"
 className="font-medium text-violet-600 hover:underline text-sm flex items-center"
 >
 Visit Website
 <ExternalLink className="w-3 h-3 ml-1" />
 </a>
 </div>
 </div>
 )}
 </div>

 <div className="mt-6 pt-6 border-t border-slate-100">
 <Button 
 onClick={handleShare}
 variant="outline" 
 className="w-full"
 data-testid="share-event-btn"
 >
 <Share2 className="w-4 h-4 mr-2" />
 Share Event
 </Button>
 </div>
 </div>

 {/* Tickets Section in Sidebar - Desktop Only - ONLY for internal ticketing */}
 {event.ticketing_enabled && !event.ticket_widget_code && !event.external_ticket_url && !event.ticket_url && (
 <div className="hidden lg:block bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-6 shadow-lg">
 <h3 className="font-bold text-white mb-3 flex items-center gap-2">
 <Ticket className="w-5 h-5" />
 Get Tickets
 </h3>
 <p className="text-violet-100 text-sm mb-4">Secure your spot at this event</p>
 
 <Button 
 onClick={() => setShowTicketModal(true)}
 className="w-full bg-white text-violet-600 hover:bg-violet-50 font-bold py-3"
 data-testid="sidebar-internal-buy-tickets-btn"
 >
 <ShoppingCart className="w-4 h-4 mr-2" />
 Buy Tickets
 </Button>
 </div>
 )}
 </motion.div>
 </div>
 </div>

 {/* Ticket Purchase Modal */}
 <TicketPurchaseModal
 isOpen={showTicketModal}
 onClose={() => setShowTicketModal(false)}
 eventId={event?.event_id}
 eventTitle={event?.title}
 />

 {/* External Ticket Provider Modal (iframe or widget) */}
 <Dialog open={showExternalTicketModal} onOpenChange={setShowExternalTicketModal}>
 <DialogContent className="w-[95vw] max-w-6xl h-[90vh] p-0 overflow-hidden flex flex-col">
 <DialogHeader className="p-4 pb-2 border-b shrink-0">
 <DialogTitle className="flex items-center gap-2">
 <Ticket className="w-5 h-5 text-orange-500" />
 Get Tickets - {event?.title}
 </DialogTitle>
 <DialogDescription>
 Complete your ticket purchase below. Tickets will be delivered via the external provider.
 </DialogDescription>
 </DialogHeader>
 <div className="flex-1 overflow-auto bg-white p-6">
 {/* If widget code is provided, use it (takes priority) */}
 {event?.ticket_widget_code ? (
 <div className="min-h-full">
 <TicketWidget code={event.ticket_widget_code} />
 </div>
 ) : event?.external_ticket_url ? (
 <iframe
 src={event.external_ticket_url.startsWith('http') ? event.external_ticket_url : `https://${event.external_ticket_url}`}
 className="w-full h-full min-h-[500px] border-0"
 title="Ticket Purchase"
 allow="payment; clipboard-write"
 sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
 />
 ) : (
 <p className="text-center text-slate-500 py-8">No ticket provider configured</p>
 )}
 </div>
 <div className="p-4 border-t bg-slate-50 flex items-center justify-between shrink-0">
 <p className="text-xs text-slate-500">
 Tickets provided by external provider
 </p>
 <div className="flex gap-2">
 {event?.external_ticket_url && !event?.ticket_widget_code && (
 <a
 href={event.external_ticket_url.startsWith('http') ? event.external_ticket_url : `https://${event.external_ticket_url}`}
 target="_blank"
 rel="noopener noreferrer"
 >
 <Button variant="outline" size="sm">
 <ExternalLink className="w-3 h-3 mr-1" />
 Open in New Tab
 </Button>
 </a>
 )}
 <Button onClick={() => setShowExternalTicketModal(false)} size="sm">
 Close
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Edit URL Dialog (Admin Only) */}
 <Dialog open={editingSlug} onOpenChange={setEditingSlug}>
 <DialogContent className="sm:max-w-lg">
 <DialogHeader>
 <DialogTitle>Edit Event URL</DialogTitle>
 <DialogDescription>
 {event?.title}
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="space-y-2">
 <Label htmlFor="host_slug">Host/Club Slug</Label>
 <Input
 id="host_slug"
 placeholder="e.g., dancing-queens-club"
 value={slugForm.host_slug}
 onChange={(e) => setSlugForm({ ...slugForm, host_slug: formatSlugInput(e.target.value) })}
 className="font-mono text-sm"
 data-testid="detail-host-slug-input"
 />
 <p className="text-xs text-slate-500">
 The club or host name portion of the URL (optional). Max 60 characters.
 </p>
 </div>
 
 <div className="space-y-2">
 <Label htmlFor="event_slug">Event Slug</Label>
 <Input
 id="event_slug"
 placeholder="e.g., saturday-night-social"
 value={slugForm.slug}
 onChange={(e) => setSlugForm({ ...slugForm, slug: formatSlugInput(e.target.value) })}
 className="font-mono text-sm"
 data-testid="detail-event-slug-input"
 />
 <p className="text-xs text-slate-500">
 The event name portion of the URL. Max 60 characters.
 </p>
 </div>

 {/* URL Preview */}
 <div className="bg-slate-100 rounded-lg p-3 space-y-1">
 <p className="text-xs font-medium text-slate-600">URL Preview:</p>
 <p className="font-mono text-sm text-violet-600 break-all">
 /events/{slugForm.host_slug ? `${slugForm.host_slug}/` :''}{slugForm.slug ||'event-name'}
 </p>
 </div>

 <div className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
 <p className="font-medium text-amber-800 mb-1">Tips:</p>
 <ul className="list-disc list-inside text-xs text-amber-700 space-y-1">
 <li>Use lowercase letters, numbers, and hyphens only</li>
 <li>Spaces are automatically converted to hyphens</li>
 <li>Keep it short and descriptive for better SEO</li>
 <li>The page will redirect to the new URL after saving</li>
 </ul>
 </div>
 </div>
 <div className="flex justify-end gap-2">
 <Button variant="outline" onClick={() => setEditingSlug(false)}>
 Cancel
 </Button>
 <Button 
 onClick={handleSlugSave} 
 disabled={slugSaving || !slugForm.slug}
 data-testid="detail-save-slug-btn"
 >
 {slugSaving ?'Saving...' :'Save & Redirect'}
 </Button>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 );
}
